import type { SnapdomOptions } from '@zumer/snapdom';
import { MAX_JPEG_BASE64_CHARS } from '@unify/fhir-model/schemas/portal-bug-report.schema';

export const BUG_REPORT_EXCLUDE_ATTR = 'data-bug-report-exclude';

export function shouldIgnoreBugReportElement(el: Element): boolean {
  return Boolean(el.closest?.(`[${BUG_REPORT_EXCLUDE_ATTR}]`));
}

export function snapdomViewportOptions(): SnapdomOptions {
  return {
    clip: 'viewport',
    dpr: Math.min(window.devicePixelRatio || 1, 2),
    exclude: [`[${BUG_REPORT_EXCLUDE_ATTR}]`],
    excludeMode: 'remove',
    embedFonts: true,
    reconcile: true,
  };
}

export function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

export function stripJpegDataUrl(value: string): string {
  const trimmed = value.trim();
  const prefix = 'data:image/jpeg;base64,';
  if (trimmed.toLowerCase().startsWith(prefix)) {
    return trimmed.slice(prefix.length);
  }
  return trimmed;
}

/** JPEG SOI marker. */
export function hasJpegSignature(base64: string): boolean {
  try {
    const binary = atob(base64.slice(0, 8));
    return binary.charCodeAt(0) === 0xff && binary.charCodeAt(1) === 0xd8 && binary.charCodeAt(2) === 0xff;
  } catch {
    return false;
  }
}

export function jpegFitsCap(base64: string): boolean {
  return base64.length > 0 && base64.length <= MAX_JPEG_BASE64_CHARS;
}

function scaleCanvas(source: HTMLCanvasElement, factor: number): HTMLCanvasElement {
  const next = document.createElement('canvas');
  next.width = Math.max(1, Math.round(source.width * factor));
  next.height = Math.max(1, Math.round(source.height * factor));
  const ctx = next.getContext('2d');
  if (!ctx) {
    throw new Error('Could not capture the screen.');
  }
  // JPEG has no alpha; transparent pixels would flatten to black. Keep them white.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, next.width, next.height);
  ctx.drawImage(source, 0, 0, next.width, next.height);
  return next;
}

/**
 * Crop the capture to the painted document area and flatten onto white.
 * snapdom's viewport clip spans innerWidth/innerHeight, but the document only paints to
 * clientWidth/clientHeight (classic scrollbars occupy the difference), leaving a transparent
 * strip that JPEG encoding would otherwise turn into a black stripe.
 */
export function flattenToPaintedArea(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const doc = document.documentElement;
  const cropW = Math.min(canvas.width, Math.max(1, Math.round((doc.clientWidth / window.innerWidth) * canvas.width)));
  const cropH = Math.min(canvas.height, Math.max(1, Math.round((doc.clientHeight / window.innerHeight) * canvas.height)));
  const flat = document.createElement('canvas');
  flat.width = cropW;
  flat.height = cropH;
  const ctx = flat.getContext('2d');
  if (!ctx) {
    throw new Error('Could not capture the screen.');
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cropW, cropH);
  ctx.drawImage(canvas, 0, 0);
  return flat;
}

export function canvasToJpegBase64UnderCap(canvas: HTMLCanvasElement): string {
  const maxEdge = 1600;
  let working = canvas;
  const longEdge = Math.max(working.width, working.height);
  if (longEdge > maxEdge) {
    working = scaleCanvas(working, maxEdge / longEdge);
  }
  let quality = 0.7;
  for (let i = 0; i < 8; i++) {
    const dataUrl = working.toDataURL('image/jpeg', quality);
    const base64 = stripJpegDataUrl(dataUrl);
    if (jpegFitsCap(base64) && hasJpegSignature(base64)) {
      return base64;
    }
    quality = Math.max(0.4, quality - 0.1);
    working = scaleCanvas(working, 0.75);
  }
  throw new Error('Could not capture the screen.');
}

export function jpegBase64ToDataUrl(base64: string): string {
  return `data:image/jpeg;base64,${base64}`;
}

/**
 * Pin CSS-variable-driven padding to resolved pixel values before capture.
 * snapdom clones nodes detached from the document, so custom properties defined on ancestors
 * (e.g. Mantine's `--app-shell-navbar-offset`/`--app-shell-header-offset` used by AppShell.main's
 * `padding-inline-start`/`padding-top`) resolve to 0 in the clone, shifting content under the
 * fixed navbar/header. Inlining concrete px padding survives the clone. Returns a restore fn.
 */
function pinShellPaddingForCapture(): () => void {
  const el = document.querySelector('.mantine-AppShell-main');
  if (!(el instanceof HTMLElement)) {
    return () => {};
  }
  const cs = getComputedStyle(el);
  const prev = el.getAttribute('style');
  el.style.paddingLeft = cs.paddingLeft;
  el.style.paddingRight = cs.paddingRight;
  el.style.paddingTop = cs.paddingTop;
  el.style.paddingBottom = cs.paddingBottom;
  return () => {
    if (prev === null) {
      el.removeAttribute('style');
    } else {
      el.setAttribute('style', prev);
    }
  };
}

/**
 * Capture the current viewport. snapdom serialises the DOM into an SVG <foreignObject> and lets
 * the browser paint it, so the result matches what is on screen. Loaded only when reporting a bug.
 * Cross-origin assets without CORS may render blank.
 */
export async function captureViewportJpeg(): Promise<string> {
  const { snapdom } = await import('@zumer/snapdom');
  const options = snapdomViewportOptions();
  const restoreShellPadding = pinShellPaddingForCapture();
  let canvas: HTMLCanvasElement;
  try {
    const result = await snapdom(document.documentElement, options);
    canvas = await result.toCanvas();
  } finally {
    restoreShellPadding();
  }
  return canvasToJpegBase64UnderCap(flattenToPaintedArea(canvas));
}
